import { useState, useMemo, useEffect } from 'react';
import { FileText, Download, ChevronLeft, ChevronRight, Printer, Snowflake, TrendingDown, Palette } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AppLayout } from '@/components/layout/AppLayout';
import { useMonthlySummary } from '@/hooks/useMonthlySummary';
import { useGoals } from '@/hooks/useGoals';
import { useDebts } from '@/hooks/useDebts';
import { useProfile, type ChartTheme } from '@/hooks/useProfile';
import { 
  formatCurrency, 
  formatPercentage,
  getFocoScoreZone,
  getFocoScoreLabel,
  getFocoScoreInterpretation,
  getSavingsTrafficLight,
  getDebtTrafficLight,
  getFixedExpensesTrafficLight,
  getCashFlowTrafficLight,
  generateRecommendations,
} from '@/lib/foco-calculations';
import {
  sortBySnowball,
  calculateSnowballProjection,
  calculateSnowballSavings,
  formatMonthsToYearsMonths,
} from '@/lib/snowball-calculations';
import { cn } from '@/lib/utils';

const chartThemes: Record<ChartTheme, { name: string; stroke: string; gradientStart: string; gradientEnd: string }> = {
  coral: {
    name: 'Navy (Por defecto)',
    stroke: 'hsl(204, 64%, 47%)',
    gradientStart: 'hsl(204, 64%, 47%)',
    gradientEnd: 'hsl(204, 64%, 47%)',
  },
  ocean: {
    name: 'Océano',
    stroke: 'hsl(200, 80%, 50%)',
    gradientStart: 'hsl(200, 80%, 50%)',
    gradientEnd: 'hsl(200, 80%, 50%)',
  },
  forest: {
    name: 'Bosque',
    stroke: 'hsl(145, 63%, 49%)',
    gradientStart: 'hsl(145, 63%, 49%)',
    gradientEnd: 'hsl(145, 63%, 49%)',
  },
  purple: {
    name: 'Púrpura',
    stroke: 'hsl(270, 70%, 55%)',
    gradientStart: 'hsl(270, 70%, 55%)',
    gradientEnd: 'hsl(270, 70%, 55%)',
  },
  sunset: {
    name: 'Atardecer',
    stroke: 'hsl(35, 90%, 55%)',
    gradientStart: 'hsl(35, 90%, 55%)',
    gradientEnd: 'hsl(35, 90%, 55%)',
  },
};

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function Reports() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  const { profile, updateProfile } = useProfile();
  const [chartTheme, setChartTheme] = useState<ChartTheme>('coral');
  
  // Sync chart theme with profile
  useEffect(() => {
    if (profile?.chart_theme) {
      setChartTheme(profile.chart_theme);
    }
  }, [profile?.chart_theme]);
  
  const handleThemeChange = (theme: ChartTheme) => {
    setChartTheme(theme);
    updateProfile.mutate({ chart_theme: theme });
  };

  const { summary, hasActiveGoals } = useMonthlySummary(selectedYear, selectedMonth);
  const { goals } = useGoals();
  const { 
    debts, 
    activeDebts, 
    paidOffDebts,
    totalDebt, 
    totalPaid, 
    progressPercentage,
    totalMinimumPayment,
  } = useDebts();

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const recommendations = summary 
    ? generateRecommendations(summary, hasActiveGoals)
    : [];

  const getTrafficLightLabel = (light: 'green' | 'yellow' | 'red') => {
    const labels = { green: 'Saludable', yellow: 'Atención', red: 'Crítico' };
    return labels[light];
  };

  const getTrafficLightClass = (light: 'green' | 'yellow' | 'red') => {
    const classes = {
      green: 'bg-success/10 text-success border-success/20',
      yellow: 'bg-warning/10 text-warning border-warning/20',
      red: 'bg-destructive/10 text-destructive border-destructive/20',
    };
    return classes[light];
  };

  const activeGoals = goals.filter(g => g.status === 'active');
  
  // Snowball calculations
  const sortedDebts = sortBySnowball(activeDebts);
  const projection = calculateSnowballProjection(activeDebts, 0);
  const savings = calculateSnowballSavings(activeDebts, 0);
  const lastPayoffDate = projection.length > 0 
    ? projection[projection.length - 1]?.payoffDate 
    : null;
  const totalMonths = Math.max(...projection.map(p => p.monthsToPayoff === Infinity ? 0 : p.monthsToPayoff), 0);

  // Generate chart data for debt payoff visualization
  const debtChartData = useMemo(() => {
    if (activeDebts.length === 0) return [];
    
    const data: { month: string; balance: number; paid: number }[] = [];
    const currentDate = new Date();
    let remainingBalance = totalDebt;
    const monthlyPayment = totalMinimumPayment;
    
    // Start with current state
    data.push({
      month: currentDate.toLocaleDateString('es-CR', { month: 'short', year: '2-digit' }),
      balance: remainingBalance,
      paid: totalPaid,
    });
    
    // Project future months (up to 36 months or until paid off)
    for (let i = 1; i <= Math.min(totalMonths + 1, 36); i++) {
      const futureDate = new Date(currentDate);
      futureDate.setMonth(futureDate.getMonth() + i);
      
      remainingBalance = Math.max(0, remainingBalance - monthlyPayment);
      
      data.push({
        month: futureDate.toLocaleDateString('es-CR', { month: 'short', year: '2-digit' }),
        balance: remainingBalance,
        paid: totalPaid + (monthlyPayment * i),
      });
      
      if (remainingBalance <= 0) break;
    }
    
    return data;
  }, [activeDebts, totalDebt, totalPaid, totalMinimumPayment, totalMonths]);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-3xl font-bold">Reportes</h1>
            <p className="text-muted-foreground">
              Genera y descarga tu reporte financiero mensual
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Chart theme selector */}
            <Select value={chartTheme} onValueChange={(v) => handleThemeChange(v as ChartTheme)}>
              <SelectTrigger className="w-[160px]">
                <Palette className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Estilo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(chartThemes).map(([key, theme]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: theme.stroke }}
                      />
                      {theme.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Month selector */}
            <div className="flex items-center gap-2 bg-card rounded-lg border p-1">
              <Button variant="ghost" size="icon" onClick={handlePreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-4 py-2 font-medium min-w-[160px] text-center">
                {monthNames[selectedMonth - 1]} {selectedYear}
              </span>
              <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <Button onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Imprimir / Guardar PDF
            </Button>
          </div>
        </div>

        {/* Report Preview */}
        <div className="bg-card border rounded-2xl p-8 space-y-8 print:shadow-none print:border-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b pb-6">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-2xl">F</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Tu Dinero, Tu Poder</h2>
                <p className="text-muted-foreground">FOCO Dashboard Pro - Reporte Mensual</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold">{monthNames[selectedMonth - 1]} {selectedYear}</p>
              <p className="text-sm text-muted-foreground">Reporte generado automáticamente</p>
            </div>
          </div>

          {/* Section 1: Resumen Numérico */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Resumen Numérico
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Ingresos</p>
                <p className="text-xl font-bold text-success">{formatCurrency(summary?.total_income || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Gastos Fijos</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.total_fixed_expenses || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Gastos Variables</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.total_variable_expenses || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Ahorros</p>
                <p className="text-xl font-bold text-primary">{formatCurrency(summary?.total_savings || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Inversiones</p>
                <p className="text-xl font-bold text-primary">{formatCurrency(summary?.total_investments || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Pagos a Deudas</p>
                <p className="text-xl font-bold">{formatCurrency(summary?.total_debt_payments || 0)}</p>
              </div>
            </div>
            <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
              <p className="text-sm text-muted-foreground">Flujo Real del Mes</p>
              <p className={cn(
                'text-2xl font-bold',
                (summary?.real_cash_flow || 0) >= 0 ? 'text-success' : 'text-destructive'
              )}>
                {formatCurrency(summary?.real_cash_flow || 0)}
              </p>
            </div>
          </div>

          {/* Section 2: FOCO Score */}
          <div className="space-y-4 page-break-inside-avoid">
            <h3 className="text-xl font-bold">FOCO Score</h3>
            <div className={cn(
              'p-6 rounded-xl border',
              summary?.foco_score !== undefined && `score-zone-${getFocoScoreZone(summary.foco_score)}`
            )}>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-5xl font-bold">{summary?.foco_score || 0}</p>
                  <p className="text-sm text-muted-foreground">de 100</p>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-lg mb-2">
                    {summary?.foco_score !== undefined && getFocoScoreLabel(getFocoScoreZone(summary.foco_score))}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {summary?.foco_score !== undefined && getFocoScoreInterpretation(summary.foco_score)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Semáforos */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Semáforos Financieros</h3>
            <div className="grid grid-cols-2 gap-4">
              {summary && (
                <>
                  <div className={cn('p-4 rounded-lg border', getTrafficLightClass(getSavingsTrafficLight(summary.savings_rate)))}>
                    <p className="text-sm font-medium">Ahorro + Inversión</p>
                    <p className="text-lg font-bold">{formatPercentage(summary.savings_rate)}</p>
                    <p className="text-xs">{getTrafficLightLabel(getSavingsTrafficLight(summary.savings_rate))}</p>
                  </div>
                  <div className={cn('p-4 rounded-lg border', getTrafficLightClass(getDebtTrafficLight(summary.debt_ratio)))}>
                    <p className="text-sm font-medium">Deudas</p>
                    <p className="text-lg font-bold">{formatPercentage(summary.debt_ratio)}</p>
                    <p className="text-xs">{getTrafficLightLabel(getDebtTrafficLight(summary.debt_ratio))}</p>
                  </div>
                  <div className={cn('p-4 rounded-lg border', getTrafficLightClass(getFixedExpensesTrafficLight(summary.fixed_expense_ratio)))}>
                    <p className="text-sm font-medium">Gastos Fijos</p>
                    <p className="text-lg font-bold">{formatPercentage(summary.fixed_expense_ratio)}</p>
                    <p className="text-xs">{getTrafficLightLabel(getFixedExpensesTrafficLight(summary.fixed_expense_ratio))}</p>
                  </div>
                  <div className={cn('p-4 rounded-lg border', getTrafficLightClass(getCashFlowTrafficLight(summary.real_cash_flow)))}>
                    <p className="text-sm font-medium">Flujo Real</p>
                    <p className="text-lg font-bold">{formatCurrency(summary.real_cash_flow)}</p>
                    <p className="text-xs">{getTrafficLightLabel(getCashFlowTrafficLight(summary.real_cash_flow))}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 4: Recomendaciones */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Qué Deberías Ajustar Este Mes</h3>
            <div className="space-y-3">
              {recommendations.map((rec, index) => (
                <div key={index} className="flex gap-3 p-4 rounded-lg bg-muted/50">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <p className="text-sm">{rec}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 5: Metas */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Metas Financieras</h3>
            {activeGoals.length === 0 ? (
              <p className="text-muted-foreground">No hay metas activas registradas.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Meta</th>
                      <th className="text-right py-2 font-semibold">Actual</th>
                      <th className="text-right py-2 font-semibold">Objetivo</th>
                      <th className="text-right py-2 font-semibold">Avance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeGoals.map((goal) => {
                      const progress = goal.target_amount > 0 
                        ? (Number(goal.current_amount) / Number(goal.target_amount)) * 100
                        : 0;
                      return (
                        <tr key={goal.id} className="border-b">
                          <td className="py-3">{goal.name}</td>
                          <td className="py-3 text-right">{formatCurrency(Number(goal.current_amount))}</td>
                          <td className="py-3 text-right">{formatCurrency(Number(goal.target_amount))}</td>
                          <td className="py-3 text-right font-semibold text-primary">{progress.toFixed(0)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Section 6: Bola de Nieve - Progreso de Deudas */}
          {debts.length > 0 && (
            <div className="space-y-4 page-break-inside-avoid">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Snowflake className="h-5 w-5 text-primary" />
                Bola de Nieve — Progreso de Deudas
              </h3>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Deuda Total</p>
                  <p className="text-xl font-bold text-destructive">{formatCurrency(totalDebt)}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Total Pagado</p>
                  <p className="text-xl font-bold text-success">{formatCurrency(totalPaid)}</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Progreso Total</p>
                  <p className="text-xl font-bold text-primary">{progressPercentage.toFixed(1)}%</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Libre de deudas en</p>
                  <p className="text-xl font-bold">{formatMonthsToYearsMonths(totalMonths)}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progreso general</span>
                  <span className="font-medium">{progressPercentage.toFixed(1)}%</span>
                </div>
                <Progress value={progressPercentage} className="h-3" />
              </div>

              {/* Debt Payoff Chart */}
              {debtChartData.length > 1 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Proyección de pago de deudas en el tiempo</p>
                  <div className="h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={debtChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={chartThemes[chartTheme].gradientStart} stopOpacity={0.25}/>
                            <stop offset="95%" stopColor={chartThemes[chartTheme].gradientEnd} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="4 4" stroke="hsl(220, 13%, 91%)" vertical={false} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 10, fill: 'hsl(213, 15%, 52%)' }} 
                          interval="preserveStartEnd"
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: 'hsl(213, 15%, 52%)' }} 
                          tickFormatter={(value) => `₡${(value / 1000).toFixed(0)}k`}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip 
                          formatter={(value: number) => [formatCurrency(value), 'Saldo']}
                          labelClassName="font-medium"
                          contentStyle={{ 
                            backgroundColor: 'hsl(220, 30%, 98%)',
                            border: '1px solid hsl(220, 13%, 91%)',
                            borderRadius: '10px',
                            boxShadow: '0 4px 20px -4px rgba(27, 42, 74, 0.12)',
                            fontSize: '13px',
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="balance" 
                          stroke={chartThemes[chartTheme].stroke}
                          fill="url(#balanceGradient)"
                          strokeWidth={2.5}
                          name="Saldo restante"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Basado en pagos mínimos mensuales de {formatCurrency(totalMinimumPayment)}
                  </p>
                </div>
              )}

              {/* Savings Info */}
              {savings.interestSaved > 0 && (
                <div className="p-4 rounded-lg border-2 border-success/20 bg-success/5">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown className="h-4 w-4 text-success" />
                    <p className="text-sm font-medium text-success">Ahorro proyectado con Bola de Nieve</p>
                  </div>
                  <p className="text-2xl font-bold text-success">{formatCurrency(savings.interestSaved)}</p>
                  <p className="text-xs text-muted-foreground">en intereses vs. solo pagos mínimos</p>
                </div>
              )}

              {/* Debts Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Orden</th>
                      <th className="text-left py-2 font-semibold">Deuda</th>
                      <th className="text-right py-2 font-semibold">Saldo</th>
                      <th className="text-right py-2 font-semibold">Pago Mín.</th>
                      <th className="text-right py-2 font-semibold">Avance</th>
                      <th className="text-right py-2 font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDebts.map((debt, index) => {
                      const debtProgress = debt.initial_balance > 0 
                        ? ((debt.initial_balance - debt.current_balance) / debt.initial_balance) * 100
                        : 0;
                      return (
                        <tr key={debt.id} className="border-b">
                          <td className="py-3">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3">
                            <div>
                              <p className="font-medium">{debt.name}</p>
                              {debt.creditor && <p className="text-xs text-muted-foreground">{debt.creditor}</p>}
                            </div>
                          </td>
                          <td className="py-3 text-right font-medium">{formatCurrency(debt.current_balance)}</td>
                          <td className="py-3 text-right">{formatCurrency(debt.minimum_payment)}</td>
                          <td className="py-3 text-right font-semibold text-primary">{debtProgress.toFixed(0)}%</td>
                          <td className="py-3 text-right">
                            {index === 0 ? (
                              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">🎯 Próxima</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">En cola</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {paidOffDebts.map((debt) => (
                      <tr key={debt.id} className="border-b opacity-60">
                        <td className="py-3">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white text-xs">✓</span>
                        </td>
                        <td className="py-3">
                          <p className="font-medium line-through">{debt.name}</p>
                        </td>
                        <td className="py-3 text-right">{formatCurrency(0)}</td>
                        <td className="py-3 text-right">—</td>
                        <td className="py-3 text-right font-semibold text-success">100%</td>
                        <td className="py-3 text-right">
                          <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success font-medium">✅ Pagada</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Projection Timeline */}
              {projection.length > 0 && (
                <div className="mt-4 p-4 rounded-lg bg-muted/30">
                  <p className="text-sm font-medium mb-2">Proyección de pago:</p>
                  <div className="flex flex-wrap gap-2">
                    {projection.slice(0, 5).map((item, index) => (
                      <span key={item.debtId} className="text-xs px-2 py-1 rounded bg-background border">
                        {index + 1}. {item.debtName}: {item.payoffDate.toLocaleDateString('es-CR', { month: 'short', year: 'numeric' })}
                      </span>
                    ))}
                    {projection.length > 5 && (
                      <span className="text-xs px-2 py-1 text-muted-foreground">+{projection.length - 5} más</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="pt-6 border-t text-center text-sm text-muted-foreground">
            <p>Reporte generado por FOCO Dashboard Pro</p>
            <p>Tu Dinero, Tu Poder — {new Date().toLocaleDateString('es-ES')}</p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
